import { useContext, useState } from 'react';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { client } from '../api';
import type { ReferralFormData } from '../types.ts';
import { ReferralForm } from '../components/referral/ReferralForm/ReferralForm.tsx';
import { ReferralSuccessModal } from '../components/referral/ReferralSuccessModal/ReferralSuccessModal.tsx';
import PageContainer from '../components/PageContainer.tsx';
import { AuthContext } from '../auth.tsx';

const BodyContainer = styled.div`
  padding: 1.25rem 1.5rem 1.5rem 1.5rem;
  margin: 0 auto;
  width: fit-content;

  @media screen and (max-width: 768px) {
    margin: 0;
  }
`;

/**
 * The page where a user refers another provider to Tandem.
 */
const ReferralPage = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const authContext = useContext(AuthContext);

  const defaultFormData: ReferralFormData = {
    referrer_name: '',
    referrer_email: authContext.user?.email ?? '',
    referred_practice_name: '',
    referred_contact_name: '',
    referred_contact_role: '',
    referred_contact_email: '',
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    getValues,
  } = useForm<ReferralFormData>({
    defaultValues: defaultFormData,
  });

  const submit = useMutation<{}, AxiosError<{ error: string }>, ReferralFormData>({
    mutationFn: async (formData) => {
      // TODO: update path
      return client.post('/referral/submit', formData).then((res) => res.data);
    },
    onSuccess: () => {
      setShowSuccessModal(true);
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    reset((formValues) => ({
      ...formValues,
      // only reset the referred fields so the user can refer another practice using their
      // own information
      referred_practice_name: '',
      referred_contact_name: '',
      referred_contact_role: '',
      referred_contact_email: '',
    }));
  };

  return (
    <PageContainer>
      <BodyContainer>
        <ReferralForm
          onSubmit={handleSubmit((data) => submit.mutate(data))}
          errors={errors}
          control={control}
          isSubmitting={submit.isPending}
        />
      </BodyContainer>

      {showSuccessModal && (
        <ReferralSuccessModal
          onClose={handleCloseSuccessModal}
          referredContactEmail={getValues('referred_contact_email')}
        />
      )}
    </PageContainer>
  );
};

export default ReferralPage;
